<template>
    <div>
        <b-card title="Mail Send Logs">
            <b-row class="mb-2">
                <b-col md="3">
                    <b-form-group label="Type" label-for="type-filter">
                        <b-form-select
                            id="type-filter"
                            v-model="typeFilter"
                            :options="typeOptions"
                            :disabled="isLoading"
                            @change="onFilterChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Status" label-for="status-filter">
                        <b-form-select
                            id="status-filter"
                            v-model="statusFilter"
                            :options="statusOptions"
                            :disabled="isLoading"
                            @change="onFilterChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="Search (email / subject)" label-for="search-filter">
                        <b-form-input
                            id="search-filter"
                            v-model="search"
                            placeholder="Type to search..."
                            :disabled="isLoading"
                            @keyup.enter="onFilterChange"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="2" class="d-flex align-items-end mb-1">
                    <b-button variant="primary" block :disabled="isLoading" @click="onFilterChange">
                        Search
                    </b-button>
                </b-col>
            </b-row>

            <b-table
                striped
                hover
                :items="logs"
                :fields="fields"
                responsive="sm"
                :busy="isLoading"
                @row-clicked="openDetail"
            >
                <template #cell(type)="data">
                    <b-badge :variant="typeVariant(data.item.type)">{{ typeLabel(data.item.type) }}</b-badge>
                </template>
                <template #cell(status)="data">
                    <b-badge :variant="data.item.status === 'success' ? 'success' : 'danger'">
                        {{ data.item.status }}
                    </b-badge>
                </template>
                <template #cell(to_email)="data">
                    <span class="text-truncate d-inline-block" style="max-width: 240px;">{{ data.item.to_email }}</span>
                </template>
                <template #cell(created_at)="data">
                    {{ formatDate(data.item.created_at) }}
                </template>
                <template #cell(actions)="data">
                    <b-button size="sm" variant="outline-primary" @click.stop="openDetail(data.item)">
                        View
                    </b-button>
                </template>
            </b-table>

            <b-pagination
                v-model="currentPage"
                :total-rows="totalRows"
                :per-page="perPage"
                align="right"
                class="mt-3"
                @change="fetchLogs"
            />
        </b-card>

        <b-modal v-model="showDetail" title="Mail Send Log Detail" size="lg" ok-only ok-title="Close">
            <div v-if="selected">
                <p><strong>Type:</strong> {{ typeLabel(selected.type) }}</p>
                <p>
                    <strong>Status:</strong>
                    <b-badge :variant="selected.status === 'success' ? 'success' : 'danger'">
                        {{ selected.status }}
                    </b-badge>
                </p>
                <p><strong>To:</strong> {{ selected.to_email || '-' }}</p>
                <p><strong>Subject:</strong> {{ selected.subject || '-' }}</p>
                <p><strong>Message ID:</strong> {{ selected.message_id || '-' }}</p>
                <p><strong>Sent At:</strong> {{ formatDate(selected.created_at) }}</p>
                <p v-if="selected.error_message" class="text-danger">
                    <strong>Error:</strong> {{ selected.error_message }}
                </p>

                <b-tabs v-if="selected.status === 'success'" class="mt-2">
                    <b-tab title="Accepted">
                        <pre class="log-detail-pre">{{ pretty(selected.accepted) }}</pre>
                    </b-tab>
                    <b-tab title="Rejected">
                        <pre class="log-detail-pre">{{ pretty(selected.rejected) }}</pre>
                    </b-tab>
                    <b-tab title="Envelope">
                        <pre class="log-detail-pre">{{ pretty(selected.envelope) }}</pre>
                    </b-tab>
                    <b-tab title="Response">
                        <pre class="log-detail-pre">{{ selected.response || '-' }}</pre>
                    </b-tab>
                </b-tabs>
            </div>
        </b-modal>
    </div>
</template>

<script>
import {
    BCard, BTable, BBadge, BPagination, BFormSelect, BFormGroup, BFormInput,
    BButton, BRow, BCol, BModal, BTabs, BTab,
} from 'bootstrap-vue'
import axios from '@axios'

export default {
    components: {
        BCard,
        BTable,
        BBadge,
        BPagination,
        BFormSelect,
        BFormGroup,
        BFormInput,
        BButton,
        BRow,
        BCol,
        BModal,
        BTabs,
        BTab,
    },
    data() {
        return {
            logs: [],
            fields: [
                { key: 'type', label: 'Type' },
                { key: 'to_email', label: 'To' },
                { key: 'subject', label: 'Subject' },
                { key: 'status', label: 'Status' },
                { key: 'created_at', label: 'Sent At' },
                { key: 'actions', label: '' },
            ],
            currentPage: 1,
            perPage: 15,
            totalRows: 0,
            isLoading: false,
            typeFilter: '',
            statusFilter: '',
            search: '',
            showDetail: false,
            selected: null,
            typeOptions: [
                { value: '', text: 'All Types' },
                { value: 'save_search', text: 'Save Search' },
                { value: 'property_checklist', text: 'Property Checklist' },
                { value: 'property_approve', text: 'Property Approve' },
                { value: 'forgot_password', text: 'Forgot Password' },
                { value: 'enquiry_update', text: 'Enquiry Update' },
            ],
            statusOptions: [
                { value: '', text: 'All' },
                { value: 'success', text: 'Success' },
                { value: 'error', text: 'Error' },
            ],
        }
    },
    methods: {
        typeLabel(type) {
            const found = this.typeOptions.find(o => o.value === type)
            return found ? found.text : type
        },
        typeVariant(type) {
            switch (type) {
                case 'save_search': return 'info'
                case 'property_checklist': return 'warning'
                case 'property_approve': return 'primary'
                case 'forgot_password': return 'danger'
                case 'enquiry_update': return 'success'
                default: return 'secondary'
            }
        },
        formatDate(value) {
            if (!value) return '-'
            return new Date(value).toLocaleString('en-GB', {
                timeZone: 'Asia/Bangkok',
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            })
        },
        pretty(value) {
            if (!value) return '-'
            return typeof value === 'string' ? value : JSON.stringify(value, null, 2)
        },
        async fetchLogs() {
            try {
                this.isLoading = true
                const offset = (this.currentPage - 1) * this.perPage

                const payload = {
                    perpage: this.perPage,
                    offset,
                }
                if (this.typeFilter) payload.type = this.typeFilter
                if (this.statusFilter) payload.status = this.statusFilter
                if (this.search) payload.search = this.search

                const response = await axios.post('mail-log/v1/lists', payload)

                this.logs = response?.data?.data || []
                this.totalRows = response?.data?.total || 0
            } catch (error) {
                console.error('Error loading mail logs:', error)
            } finally {
                this.isLoading = false
            }
        },
        onFilterChange() {
            this.currentPage = 1
            this.fetchLogs()
        },
        openDetail(item) {
            this.selected = item
            this.showDetail = true
        },
    },
    mounted() {
        this.fetchLogs()
    },
}
</script>

<style scoped>
.log-detail-pre {
    max-height: 320px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;
    background: rgba(0, 0, 0, 0.03);
    padding: 12px;
    border-radius: 4px;
    font-size: 12px;
}
</style>
